import { abweisen, ereignis, type Ereignis, type GespeichertesEreignis } from "../gemeinsam/index.js";
import { E } from "./ereignisse.js";
import { positionPruefen } from "./stand.js";

/**
 * Stammdaten des Reviers (Domänenmodell §4 Aggregat Revier: Notfallkontakte, Rettungspunkte, Sicherheitsbestimmungen
 * als Vorlage). Die Ereignisse sind nach K24 nachgetragen; Felder wie die gleichnamigen Befehlsfelder (befehle.md §6.2).
 * Eine Drückjagd kopiert diese Inhalte bei ihrer Anlage (Context Map „Revier → Drückjagd“).
 */
export const NOTFALLKONTAKTARTEN = ["Notruf", "Jagdleiter", "Nachsuchengespann", "Tierarzt", "Sonstiges"] as const;

export interface Notfallkontakt { Art: string; Bezeichnung: string; Telefonnummer: string; Adresse: string | null }
export interface Rettungspunkt { Kennung: string; Breite: number; "Länge": number; Lagebeschreibung: string | null }

/** Notruf 112 ist immer enthalten und weder änderbar noch entfernbar (Invariante). */
export const NOTRUF: Notfallkontakt = { Art: "Notruf", Bezeichnung: "Notruf", Telefonnummer: "112", Adresse: null };

export interface Stammdaten {
  notfallkontakte: Notfallkontakt[];
  rettungspunkte: Rettungspunkt[];
  sicherheitsbestimmungen: string[];
}

export const KEINE_STAMMDATEN: Stammdaten = { notfallkontakte: [], rettungspunkte: [], sicherheitsbestimmungen: [] };

export function stammdatenAnwenden(z: Stammdaten, e: GespeichertesEreignis): Stammdaten {
  const d = e.daten;
  switch (e.typ) {
    case E.SicherheitsbestimmungenFestgelegt:
      return { ...z, sicherheitsbestimmungen: ((d["Sicherheitsbestimmungen"] as unknown[]) ?? []).map(String) };
    case E.NotfallkontakteHinzugefuegt:
      return { ...z, notfallkontakte: [...z.notfallkontakte, ...((d["Notfallkontakte"] as Notfallkontakt[]) ?? [])] };
    case E.NotfallkontaktGeaendert:
      return {
        ...z,
        notfallkontakte: z.notfallkontakte.map((k) => k.Bezeichnung !== d["Notfallkontakt"] ? k : {
          ...k,
          Telefonnummer: d["Telefonnummer"] !== undefined ? String(d["Telefonnummer"]) : k.Telefonnummer,
          Adresse: d["Adresse"] !== undefined ? (d["Adresse"] as string | null) : k.Adresse,
        }),
      };
    case E.NotfallkontaktEntfernt:
      return { ...z, notfallkontakte: z.notfallkontakte.filter((k) => k.Bezeichnung !== d["Notfallkontakt"]) };
    case E.RettungspunkteHinzugefuegt:
      return { ...z, rettungspunkte: [...z.rettungspunkte, ...((d["Rettungspunkte"] as Rettungspunkt[]) ?? [])] };
    case E.RettungspunktGeaendert:
      return {
        ...z,
        rettungspunkte: z.rettungspunkte.map((r) => r.Kennung !== d["Rettungspunkt"] ? r : {
          ...r,
          Breite: d["Breite"] !== undefined ? Number(d["Breite"]) : r.Breite,
          "Länge": d["Länge"] !== undefined ? Number(d["Länge"]) : r["Länge"],
          Lagebeschreibung: d["Lagebeschreibung"] !== undefined ? (d["Lagebeschreibung"] as string | null) : r.Lagebeschreibung,
        }),
      };
    case E.RettungspunktEntfernt:
      return { ...z, rettungspunkte: z.rettungspunkte.filter((r) => r.Kennung !== d["Rettungspunkt"]) };
    default:
      return z;
  }
}

function text(wert: unknown, fehlt: string, max = 200): string {
  const t = typeof wert === "string" ? wert.trim() : "";
  if (!t) abweisen(fehlt);
  if (t.length > max) abweisen("unzulaessige-angaben");
  return t;
}

function optional(wert: unknown, max = 300): string | null {
  if (wert === undefined || wert === null) return null;
  if (typeof wert !== "string") abweisen("unzulaessige-angaben");
  const t = wert.trim();
  if (t.length > max) abweisen("unzulaessige-angaben");
  return t || null;
}

const RUFNUMMER = /^\+?[0-9][0-9 ()/-]*$/;

/** Telefonnummer eines Notfallkontakts: Ziffern mit üblichen Trennzeichen, auch Kurzwahlen wie 112; gespeichert wie eingegeben. */
export function rufnummerPruefen(wert: unknown): string {
  const t = typeof wert === "string" ? wert.trim() : "";
  if (!RUFNUMMER.test(t) || t.replace(/\D/g, "").length < 3 || t.length > 30) abweisen("telefonnummer-ungueltig");
  return t;
}

function gleich(a: Notfallkontakt, b: Notfallkontakt): boolean {
  return a.Art === b.Art && a.Bezeichnung === b.Bezeichnung && a.Telefonnummer === b.Telefonnummer && (a.Adresse ?? null) === (b.Adresse ?? null);
}

/** Die Notfallkontakte bei der Anlage eines Reviers: nur der Notruf. */
export function notrufBeiAnlage(handelnd: string): Ereignis {
  return ereignis(E.NotfallkontakteHinzugefuegt, { Notfallkontakte: [NOTRUF], "handelnde Person": handelnd });
}

export function notfallkontakteHinzufuegen(z: Stammdaten, eingabe: unknown, handelnd: string): Ereignis[] {
  if (!Array.isArray(eingabe)) abweisen("unzulaessige-angaben");
  const neu: Notfallkontakt[] = [];
  for (const roh of eingabe as Array<Record<string, unknown>>) {
    if (!roh || typeof roh !== "object") abweisen("unzulaessige-angaben");
    const art = text(roh["Art"], "unzulaessige-angaben");
    if (!(NOTFALLKONTAKTARTEN as readonly string[]).includes(art)) abweisen("unzulaessige-angaben");
    const k: Notfallkontakt = {
      Art: art, Bezeichnung: text(roh["Bezeichnung"], "bezeichnung-fehlt"), Telefonnummer: rufnummerPruefen(roh["Telefonnummer"]),
      Adresse: optional(roh["Adresse"]),
    };
    const vorhanden = [...z.notfallkontakte, ...neu].find((x) => x.Bezeichnung === k.Bezeichnung);
    if (vorhanden && gleich(vorhanden, k)) continue;
    if (art === "Notruf" || vorhanden?.Art === "Notruf") abweisen("notruf-nicht-aenderbar");
    if (vorhanden) abweisen("unzulaessige-angaben");
    neu.push(k);
  }
  if (neu.length === 0) return [];
  return [ereignis(E.NotfallkontakteHinzugefuegt, { Notfallkontakte: neu, "handelnde Person": handelnd })];
}

function kontakt(z: Stammdaten, bezeichnung: unknown): Notfallkontakt {
  const b = text(bezeichnung, "bezeichnung-fehlt");
  const k = z.notfallkontakte.find((x) => x.Bezeichnung === b);
  if (!k) abweisen("notfallkontakt-unbekannt");
  if (k.Art === "Notruf") abweisen("notruf-nicht-aenderbar");
  return k;
}

export function notfallkontaktAendern(z: Stammdaten, d: { Notfallkontakt?: unknown; Telefonnummer?: unknown; Adresse?: unknown }, handelnd: string): Ereignis[] {
  const k = kontakt(z, d.Notfallkontakt);
  const aenderung: Record<string, unknown> = {};
  if (d.Telefonnummer !== undefined) {
    const t = rufnummerPruefen(d.Telefonnummer);
    if (t !== k.Telefonnummer) aenderung["Telefonnummer"] = t;
  }
  if (d.Adresse !== undefined) {
    const a = optional(d.Adresse);
    if (a !== k.Adresse) aenderung["Adresse"] = a;
  }
  if (Object.keys(aenderung).length === 0) return [];
  return [ereignis(E.NotfallkontaktGeaendert, { Notfallkontakt: k.Bezeichnung, ...aenderung, "handelnde Person": handelnd })];
}

export function notfallkontaktEntfernen(z: Stammdaten, bezeichnung: unknown, handelnd: string): Ereignis[] {
  const k = kontakt(z, bezeichnung);
  return [ereignis(E.NotfallkontaktEntfernt, { Notfallkontakt: k.Bezeichnung, "handelnde Person": handelnd })];
}

export function rettungspunkteHinzufuegen(z: Stammdaten, eingabe: unknown, handelnd: string): Ereignis[] {
  if (!Array.isArray(eingabe)) abweisen("unzulaessige-angaben");
  const neu: Rettungspunkt[] = [];
  for (const roh of eingabe as Array<Record<string, unknown>>) {
    if (!roh || typeof roh !== "object") abweisen("unzulaessige-angaben");
    const kennung = text(roh["Kennung"], "kennung-fehlt", 40);
    const p = positionPruefen(roh["Breite"], roh["Länge"]);
    if ([...z.rettungspunkte, ...neu].some((r) => r.Kennung === kennung)) abweisen("rettungspunkt-kennung-vergeben");
    neu.push({ Kennung: kennung, Breite: p.breite, "Länge": p.laenge, Lagebeschreibung: optional(roh["Lagebeschreibung"]) });
  }
  if (neu.length === 0) return [];
  return [ereignis(E.RettungspunkteHinzugefuegt, { Rettungspunkte: neu, "handelnde Person": handelnd })];
}

function rettungspunkt(z: Stammdaten, kennung: unknown): Rettungspunkt {
  const k = text(kennung, "kennung-fehlt", 40);
  const r = z.rettungspunkte.find((x) => x.Kennung === k);
  if (!r) abweisen("rettungspunkt-unbekannt");
  return r;
}

/**
 * Rettungspunkt ändern (befehle.md §6.2): Breite und Länge nur zusammen, `Lagebeschreibung` `null` = ohne
 * Lagebeschreibung. Das Ereignis trägt nur die geänderten Felder; eine Position immer mit Breite und Länge.
 */
export function rettungspunktAendern(
  z: Stammdaten, d: { Rettungspunkt?: unknown; Breite?: unknown; "Länge"?: unknown; Lagebeschreibung?: unknown }, handelnd: string,
): Ereignis[] {
  const r = rettungspunkt(z, d.Rettungspunkt);
  const aenderung: Record<string, unknown> = {};
  if (d.Breite !== undefined || d["Länge"] !== undefined) {
    const p = positionPruefen(d.Breite, d["Länge"]);
    if (p.breite !== r.Breite || p.laenge !== r["Länge"]) {
      aenderung["Breite"] = p.breite;
      aenderung["Länge"] = p.laenge;
    }
  }
  if (d.Lagebeschreibung !== undefined) {
    const l = optional(d.Lagebeschreibung);
    if (l !== r.Lagebeschreibung) aenderung["Lagebeschreibung"] = l;
  }
  if (Object.keys(aenderung).length === 0) return [];
  return [ereignis(E.RettungspunktGeaendert, { Rettungspunkt: r.Kennung, ...aenderung, "handelnde Person": handelnd })];
}

export function rettungspunktEntfernen(z: Stammdaten, kennung: unknown, handelnd: string): Ereignis[] {
  const r = rettungspunkt(z, kennung);
  return [ereignis(E.RettungspunktEntfernt, { Rettungspunkt: r.Kennung, "handelnde Person": handelnd })];
}

export function sicherheitsbestimmungenFestlegen(z: Stammdaten, eingabe: unknown, handelnd: string): Ereignis[] {
  if (!Array.isArray(eingabe)) abweisen("unzulaessige-angaben");
  const liste = (eingabe as unknown[]).map((s) => text(s, "unzulaessige-angaben", 500));
  if (liste.length === z.sicherheitsbestimmungen.length && liste.every((s, i) => s === z.sicherheitsbestimmungen[i])) return [];
  return [ereignis(E.SicherheitsbestimmungenFestgelegt, { Sicherheitsbestimmungen: liste, "handelnde Person": handelnd })];
}
